import axios, { AxiosError, AxiosPromise, AxiosRequestConfig } from "axios";
import router from "@/router/router";
import { useBaseStore } from "@/stores/base";
import { checkBackendReachability } from "@/api/helpers/backendReachability";

const baseConfig: AxiosRequestConfig = {
	withCredentials: true,
	timeout: 30000,
	baseURL: `/api/`,
	adapter: "fetch",
};

export const getCsrfCookie = () => {
	return axios.create().get("/sanctum/csrf-cookie", {
		withCredentials: true,
		timeout: baseConfig.timeout,
	});
};

const createApiClient = (config: AxiosRequestConfig = {}) =>
	axios.create({ ...baseConfig, ...config });

export interface PaginationParams {
	page?: number;
	per_page?: number;
}

export interface Params {
	[key: string]: string | number | boolean | undefined;
}

export function wrapper<T>(
	axiosPromise: AxiosPromise
): Promise<Result<T, ApiError>> {
	return axiosPromise
		.then((resp) => ok<T, ApiError>(resp.data))
		.catch((e: AxiosError) => {
			if (e.code === "ERR_CANCELED") {
				return canceled<T, ApiError>(new ApiError(499, "Request canceled"));
			}

			// No response at all (timeout / network error): verify whether the
			// backend is reachable so the global unreachable overlay can show.
			if (!e.response) {
				checkBackendReachability();
			}

			// The render-dashboard page is rendered headlessly by Gotenberg and has no
			// session. Redirecting it to /login would silently stall the PDF render
			// until Gotenberg's request timeout, so let the 401 surface instead.
			if (
				e.response?.status === 401 &&
				router.currentRoute.value.path !== "/login" &&
				router.currentRoute.value.name !== "render-dashboard"
			) {
				useBaseStore().isAuthenticated = false;

				// resets everything
				window.location.href = "/login";
			}
			if (axios.isAxiosError(e) && e.response) {
				return err<T, ApiError>(
					new ApiError(
						e.response!.status,
						e.response?.data.message ?? "",
						e.response!.data.errors ?? null
					)
				);
			}

			return err<T, ApiError>(new ApiError(500, e.message, {}));
		});
}

export class ApiError {
	public errors?: Map<string, string[]>;

	public constructor(
		public status: number,
		public message?: string,
		errors?: { [key: string]: string[] }
	) {
		if (errors) {
			this.errors = new Map(Object.entries(errors));
		}
	}
}

export type Result<T, E> = Ok<T, E> | Err<T, E> | Canceled<T, E>;

export class Ok<T, E> {
	public constructor(public readonly value: T) {}

	public isOk(): this is Ok<T, E> {
		return true;
	}

	public isErr(): this is Err<T, E> {
		return false;
	}

	public isCanceled(): this is Canceled<T, E> {
		return false;
	}
}

export class Err<T, E> {
	public constructor(public readonly error: E) {}

	public isOk(): this is Ok<T, E> {
		return false;
	}

	public isErr(): this is Err<T, E> {
		return true;
	}

	public isCanceled(): this is Canceled<T, E> {
		return false;
	}
}

export class Canceled<T, E> {
	public constructor(public readonly error: E) {}

	public isOk(): this is Ok<T, E> {
		return false;
	}

	public isErr(): this is Err<T, E> {
		return false;
	}

	public isCanceled(): this is Canceled<T, E> {
		return true;
	}
}

export const ok = <T, E>(value: T): Ok<T, E> => new Ok(value);
export const err = <T, E>(error: E): Err<T, E> => new Err(error);
export const canceled = <T, E>(error: E): Canceled<T, E> => new Canceled(error);

export { createApiClient };
